import { apiFetch } from './apiClient';
export const modelRepository={
  listModels:()=>apiFetch('/models'),
  getModel:id=>apiFetch(`/models/${encodeURIComponent(id)}`),
  saveModel:input=>input?.id
    ? apiFetch(`/models/${encodeURIComponent(input.id)}`,{method:'PUT',body:JSON.stringify(input)})
    : apiFetch('/models',{method:'POST',body:JSON.stringify(input)}),
  recordAssessment:input=>apiFetch('/assessment-runs',{method:'POST',body:JSON.stringify(input)}),
  listAssessments:modelId=>apiFetch(`/models/${encodeURIComponent(modelId)}/assessment-runs`),
  getAssessment:id=>apiFetch(`/assessment-runs/${encodeURIComponent(id)}`),
  deleteModel:id=>apiFetch(`/models/${encodeURIComponent(id)}`,{method:'DELETE'}),
  duplicateModel:id=>apiFetch(`/models/${encodeURIComponent(id)}/duplicate`,{method:'POST'}),
  clearAll:()=>apiFetch('/models',{method:'DELETE'}),
  getWorkspaceProfile:()=>apiFetch('/workspace-profile'),
  saveWorkspaceProfile:profile=>apiFetch('/workspace-profile',{method:'PUT',body:JSON.stringify({profile})}),
  getModelContextDefaults:()=>apiFetch('/model-context-defaults'),
  saveModelContextDefaults:context=>apiFetch('/model-context-defaults',{method:'PUT',body:JSON.stringify({context})}),
  suggestOrganizationContext:async payload=>{
    // Prefer the dedicated endpoint when the API has been upgraded. Older/local API
    // processes may not have that route yet, so fall back to the established /ai/invoke
    // endpoint instead of surfacing a 404 to the user.
    try{
      return await apiFetch('/ai/suggest-organization-context',{method:'POST',body:JSON.stringify(payload),timeoutMs:150000});
    }catch(error){
      if(error?.status!==404) throw error;
    }

    const organizationName=String(payload?.organizationName||payload?.existingContext?.organizationName||'').trim();
    if(!organizationName) throw new Error('Enter the organization or customer name first.');
    const m=payload?.model||{};
    const existing=payload?.existingContext||{};
    const prompt=`You are helping configure Process Advisor, an AI process-advisory product. Build concise context for the named organization/customer so later process recommendations are relevant.

Organization/customer: ${organizationName}
Process: ${String(m.processType||'').trim()}
Category: ${String(m.category||'').trim()}
Description: ${String(m.description||'').trim()}
Source system: ${String(m.sourceSystem||'').trim()}

Return STRICT JSON only with exactly this shape:
{"context":{"organizationName":"","industry":"","organizationSize":"","geography":"","operatingModel":"","businessUnit":"","customerBase":"","regulatoryEnvironment":"","strategicPriorities":"","knownPainPoints":"","constraints":"","technologyLandscape":"","transformationContext":"","additionalContext":""}}

Rules:
- Preserve the supplied organization name exactly.
- Use only well-established public facts for organization-specific claims.
- Do not infer internal pain points, constraints, technologies, strategy, operating practices or transformation programs from the organization name.
- If an internal fact is not publicly knowable, leave it blank. Conservative sector context may be used only when explicitly prefixed "General sector context:".
- Keep observation and sector inference separate; never phrase a sector assumption as an organization fact.
- Do not overwrite or contradict user-provided values: ${JSON.stringify(existing)}.
- Keep values concise and useful; no markdown.`;
    const result=await apiFetch('/ai/invoke',{method:'POST',timeoutMs:150000,body:JSON.stringify({
      prompt,reportLabel:'Organization context suggestion',responseFormat:'json',includeStoredContext:false,temperature:0.2,
    })});
    const raw=String(result?.text||'').trim().replace(/^```(?:json)?\s*/i,'').replace(/```$/,'').trim();
    let parsed;
    try{parsed=JSON.parse(raw);}catch(_){
      const a=raw.indexOf('{'),b=raw.lastIndexOf('}');
      if(a>=0&&b>a){try{parsed=JSON.parse(raw.slice(a,b+1));}catch(__){}}
    }
    if(!parsed) throw new Error('AI returned an invalid organization context response.');
    const context=parsed?.context||parsed||{};
    context.organizationName=organizationName;
    return {context,provider:result?.provider,model:result?.model,generatedAt:new Date().toISOString()};
  },
  getAccessStatus:()=>apiFetch('/access/status'),
  authorizeAssessment:payload=>apiFetch('/access/authorize-assessment',{method:'POST',body:JSON.stringify(payload)}),
  activateAssessment:(modelId,payload={})=>apiFetch(`/models/${encodeURIComponent(modelId)}/activate-assessment`,{method:'POST',body:JSON.stringify(payload)}),
  getBillingOfferings:()=>apiFetch('/billing/offerings'),
  startBillingCheckout:(priceId,currency='aud')=>apiFetch('/billing/checkout',{method:'POST',body:JSON.stringify({priceId,currency})}),
  syncBillingCheckout:sessionId=>apiFetch('/billing/checkout/sync',{method:'POST',body:JSON.stringify({sessionId})}),
  getCreditLedger:()=>apiFetch('/billing/credit-ledger'),
  getBillingInvoices:()=>apiFetch('/billing/invoices'),
  openBillingCustomerPortal:()=>apiFetch('/billing/customer-portal',{method:'POST'}),
  registerBilling:payload=>apiFetch('/billing/register',{method:'POST',body:JSON.stringify(payload)}),
  refreshBilling:credentialType=>apiFetch('/billing/refresh',{method:'POST',body:JSON.stringify({credentialType})}),
  removeBilling:credentialType=>apiFetch(`/billing/connection/${encodeURIComponent(credentialType||'')}`,{method:'DELETE'}),
  getAISettings:()=>apiFetch('/ai/settings'),
  saveAISettings:payload=>apiFetch('/ai/settings',{method:'PUT',body:JSON.stringify(payload)}),
  testAI:()=>apiFetch('/ai/test',{method:'POST'}),
  suggestRules:payload=>apiFetch('/ai/suggest-rules',{method:'POST',body:JSON.stringify(payload),timeoutMs:150000}),
  getAIPromptContextAudit:modelId=>apiFetch(`/models/${encodeURIComponent(modelId)}/ai-prompt-context-audit`),
  listSourceConnections:()=>apiFetch('/source-connections'),
  createSourceConnection:payload=>apiFetch('/source-connections',{method:'POST',body:JSON.stringify(payload)}),
  updateSourceConnection:(id,payload)=>apiFetch(`/source-connections/${encodeURIComponent(id)}`,{method:'PUT',body:JSON.stringify(payload)}),
  testSourceConnection:id=>apiFetch(`/source-connections/${encodeURIComponent(id)}/test`,{method:'POST'}),
  testSourceConnectionInput:payload=>apiFetch('/source-connection-tests',{method:'POST',body:JSON.stringify(payload)}),
  deleteSourceConnection:id=>apiFetch(`/source-connections/${encodeURIComponent(id)}`,{method:'DELETE'}),
  getJiraMetadata:id=>apiFetch(`/source-connections/${encodeURIComponent(id)}/jira/metadata`),
  countJiraScope:(id,payload)=>apiFetch(`/source-connections/${encodeURIComponent(id)}/jira/count`,{method:'POST',body:JSON.stringify(payload),timeoutMs:90000}),
  previewJiraScope:(id,payload)=>apiFetch(`/source-connections/${encodeURIComponent(id)}/jira/preview`,{method:'POST',body:JSON.stringify(payload)}),
  extractJiraModel:(id,payload)=>apiFetch(`/source-connections/${encodeURIComponent(id)}/jira/extract`,{method:'POST',body:JSON.stringify(payload),timeoutMs:300000}),
  getServiceNowMetadata:(id,table='incident')=>apiFetch(`/source-connections/${encodeURIComponent(id)}/servicenow/metadata?table=${encodeURIComponent(table)}`),
  countServiceNowScope:(id,payload)=>apiFetch(`/source-connections/${encodeURIComponent(id)}/servicenow/count`,{method:'POST',body:JSON.stringify(payload),timeoutMs:90000}),
  previewServiceNowScope:(id,payload)=>apiFetch(`/source-connections/${encodeURIComponent(id)}/servicenow/preview`,{method:'POST',body:JSON.stringify(payload)}),
  extractServiceNowModel:(id,payload)=>apiFetch(`/source-connections/${encodeURIComponent(id)}/servicenow/extract`,{method:'POST',body:JSON.stringify(payload),timeoutMs:300000}),
  getBackendInfo:async()=>({mode:'database',database:'process_advisor'}),
};
